import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Label, Reveal } from "@/components/common/Reveal";
import { ProjectMedia } from "@/components/common/ProjectMedia";
import { StatusTag } from "@/components/projects/ProjectCard";
import { CtaBand } from "@/components/sections/CtaBand";
import { PROJECTS, TYPE_LABEL, STATUS_LABEL } from "@/data/site";
import NotFound from "./NotFound";

const Spec = ({ k, v, testid }) => (
  <div className="grid grid-cols-[9rem_1fr] gap-4 border-b border-canvas-line py-4" data-testid={testid}>
    <dt className="label-mono pt-1 text-steel">{k}</dt>
    <dd className="text-ink">{v}</dd>
  </div>
);

const Pager = ({ index }) => {
  const prev = PROJECTS[(index - 1 + PROJECTS.length) % PROJECTS.length];
  const next = PROJECTS[(index + 1) % PROJECTS.length];
  return (
    <div className="grid border-t border-canvas-line sm:grid-cols-2">
      <Link to={`/projects/${prev.slug}`} className="group flex items-center gap-4 border-canvas-line p-6 hover:bg-canvas-800 sm:border-r sm:p-8" data-testid="project-prev-link">
        <ArrowLeft className="h-5 w-5 text-brand" />
        <span><span className="label-mono block text-steel">Previous</span><span className="font-display text-2xl font-bold uppercase">{prev.name}</span></span>
      </Link>
      <Link to={`/projects/${next.slug}`} className="group flex items-center justify-end gap-4 border-t border-canvas-line p-6 text-right hover:bg-canvas-800 sm:border-t-0 sm:p-8" data-testid="project-next-link">
        <span><span className="label-mono block text-steel">Next</span><span className="font-display text-2xl font-bold uppercase">{next.name}</span></span>
        <ArrowRight className="h-5 w-5 text-brand" />
      </Link>
    </div>
  );
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const index = PROJECTS.findIndex((p) => p.slug === slug);
  if (index < 0) return <NotFound />;
  const p = PROJECTS[index];

  return (
    <main data-testid="project-detail-page">
      <section className="cad-grid border-b border-canvas-line pt-32 pb-14 sm:pt-40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Link to="/projects" className="label-mono inline-flex items-center gap-2 text-steel hover:text-brand" data-testid="project-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> All projects
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Label>{p.ref}</Label>
            <StatusTag status={p.status} />
          </div>
          <h1 className="mt-6 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-6xl lg:text-7xl" data-testid="project-title">{p.name}</h1>
          <p className="mt-4 text-lg text-steel">{p.location}</p>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20">
        <Reveal>
          <ProjectMedia project={p} className="corner-ticks aspect-[4/3] md:aspect-[16/9]" />
        </Reveal>
        <div className="mt-14 grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <Label>Project summary</Label>
            <p className="mt-6 text-lg leading-relaxed text-ink/90 sm:text-xl" data-testid="project-summary">{p.summary}</p>
          </Reveal>
          <Reveal className="lg:col-span-5 lg:col-start-8">
            <Label>Spec sheet</Label>
            <dl className="mt-6 border-t border-canvas-line">
              <Spec k="Location" v={p.location} testid="spec-location" />
              {p.client && <Spec k="Client" v={p.client} testid="spec-client" />}
              {p.contractor && <Spec k="Main contractor" v={p.contractor} testid="spec-contractor" />}
              <Spec k="Units" v={p.units ? `${p.units}` : "To be confirmed"} testid="spec-units" />
              <Spec k="Type" v={TYPE_LABEL[p.type]} testid="spec-type" />
              <Spec k="Status" v={STATUS_LABEL[p.status]} testid="spec-status" />
              <Spec k="Scope" testid="spec-scope" v={p.scope.length ? (
                <ul className="space-y-1">{p.scope.map((s) => <li key={s} className="flex gap-2"><span className="text-brand">—</span>{s}</li>)}</ul>
              ) : "To be confirmed"} />
            </dl>
          </Reveal>
        </div>
      </section>
      <Pager index={index} />
      <CtaBand testid="project-cta" title="Have a similar scheme?" />
    </main>
  );
}
